import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import {
  customerApiErrorMessage,
  listMyVouchers,
  type CustomerVoucherRow,
} from '../../lib/customer-api';
import { RequireCustomer } from '../../lib/use-customer-auth';
import { toast } from '../../lib/toast';

type TabType = 'available' | 'used' | 'expired';

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    });
  } catch {
    return '—';
  }
};

const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

const buildTitle = (v: CustomerVoucherRow): string => {
  if (v.voucher?.title) return v.voucher.title;
  if (!v.voucher) return 'Voucher';
  if (v.voucher.discount_type === 'PERCENT') {
    return `${v.voucher.discount_value}% off voucher`;
  }
  return `${formatMoney(v.voucher.discount_value, v.voucher.currency)} voucher`;
};

export default function VoucherPage() {
  return (
    <RequireCustomer>
      <VoucherPageInner />
    </RequireCustomer>
  );
}

function VoucherPageInner() {
  const [activeTab, setActiveTab] = useState<TabType>('available');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [available, setAvailable] = useState<CustomerVoucherRow[]>([]);
  const [used, setUsed] = useState<CustomerVoucherRow[]>([]);
  const [expired, setExpired] = useState<CustomerVoucherRow[]>([]);

  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listMyVouchers();
      setAvailable(res.available ?? []);
      setUsed(res.used ?? []);
      setExpired(res.expired ?? []);
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Could not load your vouchers.');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCopy = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const tabs: { key: TabType; label: string; count: number }[] = [
    { key: 'available', label: 'Available', count: available.length },
    { key: 'used', label: 'Used', count: used.length },
    { key: 'expired', label: 'Expired', count: expired.length },
  ];

  const rows =
    activeTab === 'available'
      ? available
      : activeTab === 'used'
        ? used
        : expired;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-12">
        {/* Back and Title */}
        <div className="flex items-start gap-4 mb-8">
          <Link
            to="/account-settings"
            className="mt-1 w-[40px] h-[40px] bg-[#3a3335]/[0.04] rounded-[10px] flex items-center justify-center hover:bg-[#3a3335]/[0.08] transition-colors shrink-0"
          >
            <span className="material-icons text-[#908e8e] text-[18px]">
              chevron_left
            </span>
          </Link>
          <div>
            <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px] mb-1">
              Vouchers
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              Discount codes assigned to your account
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-3 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`h-[42px] px-5 rounded-[8px] font-['DM_Sans',sans-serif] font-bold text-[14px] tracking-[-0.1px] transition-all duration-200 ${
                activeTab === tab.key
                  ? 'bg-white border border-[#8e2a2f] text-[#8e2a2f]'
                  : 'bg-[#3a3335]/[0.04] text-[#908e8e] hover:bg-[#3a3335]/[0.08]'
              }`}
            >
              {tab.label}
              {!loading ? (
                <span className="ml-2 opacity-70">({tab.count})</span>
              ) : null}
            </button>
          ))}
        </div>

        {/* Body */}
        {loading ? (
          <div className="py-20 text-center text-[#908e8e] font-['DM_Sans',sans-serif] text-[14px]">
            Loading your vouchers…
          </div>
        ) : rows.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[14px] mb-10">
            {rows.map((row) => {
              const expiredCard = row.effective_status === 'EXPIRED';
              const usedCard = row.effective_status === 'USED';
              return (
                <div
                  key={row.id}
                  className={`relative rounded-[8px] border border-[#eee] p-5 flex flex-col justify-between min-h-[125px] ${
                    expiredCard ? 'opacity-50' : ''
                  }`}
                >
                  {/* Top row: title + code / status pill */}
                  <div className="flex items-start justify-between gap-4">
                    <p className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px] leading-[27px]">
                      {buildTitle(row)}
                    </p>
                    {row.effective_status === 'AVAILABLE' && row.voucher ? (
                      <div
                        onClick={() => handleCopy(row.voucher!.code, row.id)}
                        className="cursor-pointer group flex items-center gap-2 px-3 py-1.5 border-2 border-dashed border-[#8e2a2f]/[0.3] hover:border-[#8e2a2f] rounded-[6px] transition-all duration-200"
                      >
                        <span className="font-['DM_Sans',sans-serif] font-extrabold text-[#8e2a2f] text-[14px] tracking-[1px]">
                          {row.voucher.code}
                        </span>
                        <span className="material-icons text-[16px] text-[#8e2a2f]">
                          {copiedId === row.id ? 'check' : 'content_copy'}
                        </span>
                      </div>
                    ) : null}
                    {usedCard ? (
                      <span className="shrink-0 h-[31px] px-5 rounded-[30px] bg-[#3a3335]/[0.06] text-[#908e8e] font-['DM_Sans',sans-serif] font-bold text-[14px] flex items-center">
                        Used
                      </span>
                    ) : null}
                    {expiredCard ? (
                      <span className="shrink-0 h-[31px] px-5 rounded-[30px] bg-[#3a3335]/[0.06] text-[#908e8e] font-['DM_Sans',sans-serif] font-bold text-[14px] flex items-center">
                        Expired
                      </span>
                    ) : null}
                  </div>

                  {/* Bottom: description + validity */}
                  <div className="mt-3">
                    {row.voucher?.description ? (
                      <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] leading-[21px] tracking-[-0.1px]">
                        {row.voucher.description}
                      </p>
                    ) : null}
                    {usedCard && row.redeemed_at ? (
                      <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] leading-[18px] tracking-[-0.1px]">
                        {`Redeemed on ${formatDate(row.redeemed_at)}`}
                      </p>
                    ) : (
                      <>
                        {row.voucher?.valid_from ? (
                          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] leading-[18px] tracking-[-0.1px]">
                            {`Valid from ${formatDate(row.voucher.valid_from)}`}
                          </p>
                        ) : null}
                        <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] leading-[18px] tracking-[-0.1px]">
                          {row.expires_at
                            ? `Valid until ${formatDate(row.expires_at)}`
                            : 'No expiry date'}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="material-icons text-[48px] text-[#eee] mb-4">
              confirmation_number
            </span>
            <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[16px] mb-1">
              No {activeTab} vouchers
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              Your {activeTab} vouchers will appear here.
            </p>
          </div>
        )}

        {/* Terms & Conditions */}
        <div className="bg-[#f2eee2] rounded-[6px] shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] p-8">
          <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[16px] leading-[24px] mb-4">
            Terms &amp; Conditions
          </h2>
          <ul className="list-disc pl-5 space-y-[3px] font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] leading-[21px] tracking-[-0.1px]">
            <li>Active vouchers will be deducted from your total booking.</li>
            <li>
              Can be used across all services (cleaning, childcare, beauty
              &amp; wellness)
            </li>
            <li>
              Valid for one-time or multiple bookings until balance is used
            </li>
            <li>Non-refundable and cannot be exchanged for cash</li>
            <li>Cannot be combined with other promotions</li>
          </ul>
        </div>
      </main>

      <Footer />
    </div>
  );
}
